/**
 * IDDLAIDecision — Phase 2 · Responsible AI Use primitive renderer.
 *
 * Purpose: elicit Responsible AI Use competency. The candidate must
 * decide WHETHER and HOW to use AI in a specific work context. A
 * competent candidate must not be forced to use AI — declining or
 * escalating are valid responses. The primitive assesses JUDGMENT,
 * not frequency of AI use. Maximum restriction is not automatically
 * competent. Maximum autonomy is not automatically incompetent.
 *
 * Scenario schema (all content comes from concept.scenario_text —
 * no domain or organisation content is embedded in this file):
 *
 *   {
 *     task_brief: string,
 *     context: string,
 *     data_inventory: [
 *       { id, label, sensitive?: bool }
 *     ],
 *     options: [
 *       { id: "use_ai_direct"|"use_ai_after_redaction"|
 *              "use_ai_human_review"|"do_not_use_ai"|
 *              "delegate_to_human"|<custom>,
 *         label: string,
 *         details?: string }
 *     ],
 *     disclosure_required?: bool
 *   }
 *
 * Emitted events (candidate-attributed):
 *
 *   decision_workspace_opened      — mount
 *   task_reviewed                  — brief expanded
 *   option_considered              — { option_id }
 *   information_restricted         — { item_id } (per data item toggled)
 *   information_release_reset      — { item_id } (unchecked)
 *   conditional_ai_use_configured  — { redactions:[item_id], review_required:bool }
 *   ai_use_declined                — non-AI route chosen
 *   human_review_required          — planned reviewer step
 *   disclosure_planned             — { audience?: string }
 *   human_authority_retained
 *   final_decision                 — { decision: option_id, redactions, review_required, disclosure_planned }
 */
import { useEffect, useRef, useState } from "react";
import { ScrollText, ListChecks, Lock, EyeOff, UserCheck, Info, ShieldAlert, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function IDDLAIDecision({
  attemptId,
  conceptId,
  scenarioText,
  onEventLogged,
}) {
  const taskBrief = scenarioText?.task_brief || "Decide whether and how AI should participate in the task on the right.";
  const context = scenarioText?.context || "";
  const dataInventory = scenarioText?.data_inventory || [];
  const options = scenarioText?.options || [];
  const disclosureRequired = !!scenarioText?.disclosure_required;

  const [taskExpanded, setTaskExpanded] = useState(false);
  const [consideredOptions, setConsideredOptions] = useState(new Set());
  const [restrictedItems, setRestrictedItems] = useState(new Set());
  const [requireHumanReview, setRequireHumanReview] = useState(false);
  const [disclosurePlanned, setDisclosurePlanned] = useState(false);
  const [humanAuthorityRetained, setHumanAuthorityRetained] = useState(false);
  const [finalized, setFinalized] = useState(null);
  const [busy, setBusy] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("decision_workspace_opened", null, "decision_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} rejected: ${detail}`);
      return null;
    }
  }

  const reviewTask = () => {
    if (taskExpanded) return;
    setTaskExpanded(true);
    emit("task_reviewed", { context_length: context.length }, "task_brief");
  };

  const considerOption = (opt) => {
    if (consideredOptions.has(opt.id)) return;
    setConsideredOptions((s) => new Set([...s, opt.id]));
    emit("option_considered", { option_id: opt.id }, "options");
  };

  const toggleRestrict = (item) => {
    setRestrictedItems((s) => {
      const next = new Set(s);
      if (next.has(item.id)) {
        next.delete(item.id);
        emit("information_release_reset", { item_id: item.id }, "data_inventory");
      } else {
        next.add(item.id);
        emit("information_restricted", { item_id: item.id, sensitive: !!item.sensitive }, "data_inventory");
      }
      return next;
    });
  };

  const toggleHumanReview = () => {
    const next = !requireHumanReview;
    setRequireHumanReview(next);
    if (next) emit("human_review_required", null, "governance");
  };

  const toggleDisclosure = () => {
    const next = !disclosurePlanned;
    setDisclosurePlanned(next);
    if (next) emit("disclosure_planned", null, "governance");
  };

  const toggleHumanAuthority = () => {
    const next = !humanAuthorityRetained;
    setHumanAuthorityRetained(next);
    if (next) emit("human_authority_retained", null, "governance");
  };

  const finalize = async (opt) => {
    if (busy) return;
    setBusy(true);
    try {
      const isAiUse = opt.id.startsWith("use_ai");
      const conditional = isAiUse && (restrictedItems.size > 0 || requireHumanReview);

      // If this is a non-AI route, emit ai_use_declined FIRST so the
      // decision contract can weight it separately.
      if (!isAiUse) {
        await emit("ai_use_declined", { option_id: opt.id }, "finalize");
      } else if (conditional) {
        await emit(
          "conditional_ai_use_configured",
          {
            redactions: Array.from(restrictedItems),
            review_required: requireHumanReview,
          },
          "finalize",
        );
      }

      await emit(
        "final_decision",
        {
          decision: opt.id,
          redactions: Array.from(restrictedItems),
          review_required: requireHumanReview,
          disclosure_planned: disclosurePlanned,
          human_authority_retained: humanAuthorityRetained,
        },
        "finalize",
      );
      setFinalized(opt.id);
      toast.success(`Recorded final decision: ${opt.label}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="iddl-ai-decision">
      {/* Left column — task, data inventory, governance */}
      <section className="border border-[var(--iddl-rule)] bg-white">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <ScrollText size={14} />
          <span className="font-display font-semibold">Task &amp; context</span>
          {disclosureRequired ? (
            <span className="ms-auto text-xs px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-900">
              disclosure may be required
            </span>
          ) : null}
        </header>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-6" data-testid="decision-task-brief">{taskBrief}</p>

          <div className="border border-[var(--iddl-rule)]">
            <button
              type="button"
              onClick={reviewTask}
              data-testid="decision-review-task"
              className="w-full text-start px-3 py-2 text-xs flex items-center gap-2 hover:bg-zinc-50"
            >
              <Info size={12} />
              <span>{taskExpanded ? "Task context" : "Show task context"}</span>
            </button>
            {taskExpanded ? (
              <div className="px-3 py-2 border-t border-[var(--iddl-rule)] text-xs whitespace-pre-wrap leading-5"
                data-testid="decision-context-body">
                {context || "(no additional context supplied)"}
              </div>
            ) : null}
          </div>

          {dataInventory.length > 0 ? (
            <div className="border border-[var(--iddl-rule)]">
              <div className="px-3 py-2 border-b border-[var(--iddl-rule)] text-xs label-mono flex items-center gap-2">
                <EyeOff size={12} /> Information inventory · restrict before use
              </div>
              <ul className="divide-y divide-[var(--iddl-rule)]">
                {dataInventory.map((it) => (
                  <li key={it.id} className="px-3 py-2 text-xs flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={restrictedItems.has(it.id)}
                      onChange={() => toggleRestrict(it)}
                      data-testid={`decision-restrict-${it.id}`}
                    />
                    <span className="flex-1">
                      {it.label}
                      {it.sensitive ? (
                        <span className="ms-2 text-[10px] px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-900">
                          sensitive
                        </span>
                      ) : null}
                    </span>
                    {restrictedItems.has(it.id) ? (
                      <Lock size={12} className="text-emerald-700" />
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="border border-[var(--iddl-rule)] p-3 space-y-2">
            <div className="label-mono text-xs">Governance</div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={requireHumanReview}
                onChange={toggleHumanReview}
                data-testid="decision-require-human-review"
              />
              <UserCheck size={12} /> Require human review before send
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={disclosurePlanned}
                onChange={toggleDisclosure}
                data-testid="decision-plan-disclosure"
              />
              <ShieldAlert size={12} /> Disclose AI involvement to recipient
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={humanAuthorityRetained}
                onChange={toggleHumanAuthority}
                data-testid="decision-retain-authority"
              />
              <ListChecks size={12} /> Retain final human decision authority
            </label>
          </div>
        </div>
      </section>

      {/* Right column — options + final decision */}
      <section className="border border-[var(--iddl-rule)] bg-white" data-testid="decision-options-panel">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <ListChecks size={14} />
          <span className="font-display font-semibold">Choose your approach</span>
        </header>
        <ul className="divide-y divide-[var(--iddl-rule)]">
          {options.length === 0 ? (
            <li className="p-3 text-xs italic text-zinc-500">
              No options supplied in scenario_text.options. The decision primitive
              needs at least two realistic options to elicit responsible-use judgment.
            </li>
          ) : null}
          {options.map((opt) => (
            <li key={opt.id} className="p-3 space-y-2" data-testid={`decision-option-${opt.id}`}>
              <button
                type="button"
                onClick={() => considerOption(opt)}
                data-testid={`decision-consider-${opt.id}`}
                className={`w-full text-start text-xs px-2 py-1.5 border ${consideredOptions.has(opt.id) ? "bg-zinc-100 border-zinc-400" : "border-[var(--iddl-rule)]"}`}
              >
                <div className="font-semibold">{opt.label}</div>
                {opt.details ? (
                  <div className="mt-1 text-[11px] text-zinc-700 leading-5">{opt.details}</div>
                ) : null}
              </button>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || !!finalized}
                  onClick={() => finalize(opt)}
                  data-testid={`decision-finalize-${opt.id}`}
                >
                  <Send size={12} className="me-1" /> Choose this
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {finalized ? (
          <div className="px-3 py-2 border-t border-[var(--iddl-rule)] text-xs text-emerald-800">
            Recorded: <strong>{finalized}</strong>
          </div>
        ) : null}
      </section>
    </div>
  );
}
